import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiActivity, FiServer, FiClock } from 'react-icons/fi';
import clsx from 'clsx';
import { statusApi } from '../../services/api';
import { AlertInstance, SystemMetric } from '../../types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'green' | 'amber' | 'red' | 'blue';
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => {
  const colorClasses = {
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-4">
        <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center', colorClasses[color])}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [activeAlerts, setActiveAlerts] = useState<AlertInstance[]>([]);
  const [metrics, setMetrics] = useState<SystemMetric[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [alertsData, metricsData] = await Promise.all([
          statusApi.getActiveAlerts().catch(() => []),
          statusApi.getMetrics({ hours: 1 }).catch(() => []),
        ]);
        setActiveAlerts(alertsData);
        setMetrics(metricsData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  // Calculate stats
  const criticalAlerts = activeAlerts.filter(a => a.severity === 'critical').length;
  const warningAlerts = activeAlerts.filter(a => a.severity === 'warning' || a.severity === 'error').length;

  // Get latest metrics
  const latestMemory = metrics.find(m => m.metricType === 'memory_usage' && m.metricName === 'heap_alloc_mb');
  const latestDbLatency = metrics.find(m => m.metricType === 'database_health' && m.metricName === 'ping_latency_ms');
  const latestGoroutines = metrics.find(m => m.metricType === 'cpu_usage' && m.metricName === 'goroutine_count');

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Critical Alerts"
          value={criticalAlerts}
          icon={<FiAlertTriangle className="w-6 h-6" />}
          color={criticalAlerts > 0 ? 'red' : 'green'}
          subtitle={criticalAlerts > 0 ? 'Requires immediate attention' : 'All clear'}
        />
        <StatCard
          title="Warnings"
          value={warningAlerts}
          icon={<FiAlertTriangle className="w-6 h-6" />}
          color={warningAlerts > 0 ? 'amber' : 'green'}
          subtitle={warningAlerts > 0 ? 'Review recommended' : 'No warnings'}
        />
        <StatCard
          title="Memory Usage"
          value={latestMemory ? `${latestMemory.value.toFixed(1)} MB` : '--'}
          icon={<FiServer className="w-6 h-6" />}
          color={latestMemory && latestMemory.value > 500 ? 'amber' : 'blue'}
          subtitle="Heap allocated"
        />
        <StatCard
          title="DB Latency"
          value={latestDbLatency ? `${latestDbLatency.value.toFixed(0)} ms` : '--'}
          icon={<FiClock className="w-6 h-6" />}
          color={latestDbLatency && latestDbLatency.value > 100 ? 'amber' : 'green'}
          subtitle="Ping response"
        />
      </div>

      {/* Active alerts section */}
      {activeAlerts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Active Alerts</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {activeAlerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={clsx(
                      'w-3 h-3 rounded-full',
                      alert.severity === 'critical' ? 'bg-red-500' :
                      alert.severity === 'error' ? 'bg-orange-500' :
                      alert.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                    )}
                  />
                  <div>
                    <p className="font-medium text-gray-900">{alert.ruleName}</p>
                    <p className="text-sm text-gray-500">{alert.message}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-400">
                  {new Date(alert.createdAt).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System metrics overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">System Metrics</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Goroutines</p>
              <p className="text-xl font-semibold text-gray-900">
                {latestGoroutines?.value.toFixed(0) ?? '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Database Connections</p>
              <p className="text-xl font-semibold text-gray-900">
                {metrics.find(m => m.metricName === 'open_connections')?.value ?? '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">WebSocket Clients</p>
              <p className="text-xl font-semibold text-gray-900">
                {metrics.find(m => m.metricName === 'active_connections')?.value ?? '--'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
